import React, { forwardRef, useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { Relationship, TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { useAppSelector } from '@/redux/hooks';
import { selectTitleTypes } from '@/redux/selectors/constantsSelectors';

type Props = {
  title: TitleEntityV2;
  active: boolean;
  activeParent?: boolean;
  isLastChild?: boolean;
  onItemClick: (titleId: number) => void;
};

const useChildrenCount = (titleEntityType: TitleEntityV2['entity_type'], nonVersionChildren: Relationship[]) => {
  const typeMap = useAppSelector(selectTitleTypes);

  if (nonVersionChildren.length === 0) {
    return null;
  }

  if (titleEntityType === 'marketing') {
    return nonVersionChildren.length === 1 ? 'child' : 'children';
  }

  const firstChild = nonVersionChildren[0];
  const allChildrenHaveSameEntityType = nonVersionChildren.every((child) => child.entity_type === firstChild.entity_type);
  const childrenType = allChildrenHaveSameEntityType ? typeMap[firstChild.entity_type as keyof typeof typeMap]?.toLowerCase() : null;

  let childrenLabel: string;

  if (titleEntityType === 'marketing') {
    childrenLabel = nonVersionChildren.length === 1 ? 'child' : 'children';
  } else if (childrenType) {
    childrenLabel = `${childrenType}${nonVersionChildren.length > 1 ? 's' : ''}`;
  } else {
    childrenLabel = 'children';
  }

  return `${nonVersionChildren.length} ${childrenLabel}`;
};

const TitleBrowserItemV2 = forwardRef<HTMLDivElement, Props>(({ title, active, activeParent, isLastChild, onItemClick, ...props }, ref) => {
  const isSeason = title.entity_type === 'season';
  const isEpisode = title.entity_type === 'episode';
  const sequenceOrderPrefix = isSeason ? 'S' : isEpisode ? 'E' : '';
  const releaseYear = title.meta?.characteristics?.release_year;
  const nonVersionChildren = title.relationships.children.filter((c) => c.entity_type !== 'version');

  const childrenCountText = useChildrenCount(title.entity_type, nonVersionChildren);

  const handleClick = useCallback(() => {
    onItemClick(title.id);
  }, [title, onItemClick]);

  return (
    <Wrapper ref={ref} active={active} activeParent={activeParent} isLastChild={isLastChild} onClick={handleClick} {...props}>
      <Grid container alignItems="center" flexWrap="nowrap">
        <Grid item sx={{ ml: 1, mr: 1, maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {(isSeason || isEpisode) && (
            <Typography variant="subtitle2" sx={{ mr: 1 }}>
              {sequenceOrderPrefix}
              {title.sequence_order}:
            </Typography>
          )}
          {title.name}
          {releaseYear && (
            <Typography variant="caption" sx={{ ml: 1, fontSize: '.8rem' }}>
              {releaseYear}
            </Typography>
          )}
        </Grid>

        {childrenCountText && (
          <Grid item sx={{ ml: 'auto', mr: 1 }}>
            <Typography variant="caption" sx={{ fontSize: '.9rem' }}>
              {childrenCountText}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Wrapper>
  );
});

export default TitleBrowserItemV2;

const Wrapper = styled(Grid, {
  shouldForwardProp: (prop) => !['active', 'activeParent', 'isLastChild'].includes(prop)
})<{ active: boolean; activeParent: boolean | undefined; isLastChild: boolean | undefined }>`
  padding: ${({ theme }) => theme.spacing(1.5)};
  background-color: ${({ theme }) => `${theme.palette.default.main}1a`};
  border: 2px solid transparent;
  margin-bottom: ${({ theme, isLastChild }) => (isLastChild ? undefined : theme.spacing(1.25))};
  transition: ease 300ms background-color;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  ${({ theme, active }) =>
    !!active &&
    `
      background-color: ${theme.palette.primary.main}3a;
      border-color: ${theme.palette.primary.main}9a;
      & .MuiTypography-subtitle2 {
        opacity: 0.7;
      }
      &:hover {
        background-color: ${theme.palette.primary.main}3a;
      }
    `}

  ${({ theme, activeParent }) =>
    activeParent &&
    `
      border-color: ${theme.palette.primary.main}3a;
    `}

  & .MuiTypography-root {
    line-height: 1;
  }

  & .MuiTypography-subtitle2 {
    font-size: 1rem;
    display: inline-block;
    opacity: 0.5;
  }

  &:hover {
    background-color: ${({ theme }) => `${theme.palette.default.main}3a`};
    transition: ease 300ms background-color;
  }
`;
